(function () {
  if (customElements.get('horizontal-media-gallery')) return;

  class HorizontalMediaGallery extends HTMLElement {
    #controller   = new AbortController();
    #rafScheduled = false;
    #currentX     = 0;
    #hScrollRoom  = 0;
    /** @type {HTMLElement[]} */
    #panels       = [];

    connectedCallback() {
      this.#controller = new AbortController();
      requestAnimationFrame(() => this.#setup());
    }

    disconnectedCallback() {
      this.#controller.abort();
    }

    #setup() {
      const track = /** @type {HTMLElement|null} */ (this.closest('.pin-track'));
      if (!track) return;

      if (this.dataset.mode === 'single') {
        this.#initSingle(track);
      } else {
        this.#initMultiple(track);
      }
    }

    #initSingle(track) {
      const panel = /** @type {HTMLElement|null} */ (this.querySelector('.panel-single'));
      if (!panel) return;

      const isMobile = window.innerWidth <= 768;
      const activeSide = /** @type {HTMLElement} */ (
        panel.querySelector(isMobile ? ':scope > .panel-single-side.mobile' : ':scope > .panel-single-side.desktop')
        ?? panel
      );

      const media = activeSide.querySelector('img, video');
      if (!media) return;

      if (media.tagName === 'VIDEO') {
        if (/** @type {HTMLVideoElement} */ (media).readyState >= 1) {
          this.#buildSingle(track, panel);
        } else {
          media.addEventListener('loadedmetadata', () => this.#buildSingle(track, panel), {
            once: true,
            signal: this.#controller.signal,
          });
        }
        return;
      }

      if (panel.offsetWidth > 0) {
        this.#buildSingle(track, panel);
        return;
      }

      media.addEventListener('load', () => this.#buildSingle(track, panel), {
        once: true,
        signal: this.#controller.signal,
      });
    }

    #buildSingle(track, panel) {
      this.#panels = [panel];
      this.#currentX = 0;

      const recompute = () => {
        const hScrollRoom = Math.max(0, panel.offsetWidth - this.offsetWidth);
        track.style.height = `${this.offsetHeight + hScrollRoom}px`;
        this.#hScrollRoom = hScrollRoom;
      };

      recompute();
      this.#applyTransform();
      this.#bindScroll(track);

      const resizeObserver = new ResizeObserver(recompute);
      resizeObserver.observe(panel);
      this.#controller.signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });
    }

    #initMultiple(track) {
      const isMobile = window.innerWidth <= 768;
      const selector = isMobile ? '.media-mobile' : '.media-desktop';
      const panels   = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll(selector)));
      if (!panels.length) return;

      this.#panels = panels;
      this.#currentX = 0;

      const recompute = () => {
        const lastPanel   = panels[panels.length - 1];
        const hiddenWidth = Math.max(0, (lastPanel.offsetLeft + lastPanel.offsetWidth) - this.offsetWidth);

        track.style.height = `${this.offsetHeight + hiddenWidth}px`;
        this.#hScrollRoom = hiddenWidth;
      };

      recompute();
      this.#applyTransform();
      this.#bindScroll(track);

      const resizeObserver = new ResizeObserver(recompute);
      panels.forEach(p => resizeObserver.observe(p));
      this.#controller.signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });
    }

    #bindScroll(track) {
      const { signal } = this.#controller;

      const tick = () => {
        this.#rafScheduled = false;
        const scrollRoom = track.offsetHeight - this.offsetHeight;
        if (scrollRoom <= 0) return;

        const st       = track.getBoundingClientRect().top;
        const progress = Math.max(0, Math.min(1, -st / scrollRoom));
        const newX     = -progress * this.#hScrollRoom;

        if (Math.abs(newX - this.#currentX) > 0.1) {
          this.#currentX = newX;
          this.#applyTransform();
        }
      };

      const onScroll = () => {
        if (this.#rafScheduled) return;
        this.#rafScheduled = true;
        requestAnimationFrame(tick);
      };

      window.addEventListener('scroll', onScroll, { passive: true, signal });

      window.addEventListener('orientationchange', () => {
        setTimeout(() => {
          this.#controller.abort();
          this.#controller = new AbortController();
          this.#setup();
        }, 300);
      }, { passive: true, signal });

      onScroll();
    }

    #applyTransform() {
      const tx = `translateX(${this.#currentX}px)`;
      this.#panels.forEach(p => { p.style.transform = tx; });
    }
  }

  customElements.define('horizontal-media-gallery', HorizontalMediaGallery);
})();
